import React from 'react';

const emptyHighlight = () => ({ icon: 'LuStar', title: '' });
const emptyPackage = () => ({ name: '', price: '', features: [''] });

export function ServiceHighlightsField({ value = [], onChange }) {
  const items = Array.isArray(value) && value.length > 0 ? value : [emptyHighlight()];

  const updateItem = (index, field, nextValue) => {
    const nextItems = items.map((item, itemIndex) =>
      itemIndex === index ? { ...item, [field]: nextValue } : item,
    );
    onChange(nextItems);
  };

  const addItem = () => onChange([...items, emptyHighlight()]);

  const removeItem = (index) => {
    const nextItems = items.filter((_, itemIndex) => itemIndex !== index);
    onChange(nextItems.length > 0 ? nextItems : [emptyHighlight()]);
  };

  return (
    <div className="space-y-3">
      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-gray-400">
        Highlights
      </label>
      {items.map((item, index) => (
        <div
          key={index}
          className="grid grid-cols-1 gap-2 rounded-lg border border-slate-200 p-3 dark:border-white/10"
        >
          <input
            type="text"
            value={item.icon || ''}
            onChange={(e) => updateItem(index, 'icon', e.target.value)}
            placeholder="Icon (e.g. LuPalette)"
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white"
          />
          <input
            type="text"
            value={item.title || ''}
            onChange={(e) => updateItem(index, 'title', e.target.value)}
            placeholder="Highlight title"
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white"
          />
          {items.length > 1 ? (
            <button
              type="button"
              onClick={() => removeItem(index)}
              className="justify-self-start text-xs font-bold text-red-500"
            >
              Remove highlight
            </button>
          ) : null}
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        className="rounded-lg border border-primary/40 px-3 py-1.5 text-xs font-bold text-primary"
      >
        + Add Highlight
      </button>
    </div>
  );
}

export function ServicePackagesField({ value = [], onChange }) {
  const items = Array.isArray(value) && value.length > 0 ? value : [emptyPackage()];

  const updateItem = (index, field, nextValue) => {
    const nextItems = items.map((item, itemIndex) =>
      itemIndex === index ? { ...item, [field]: nextValue } : item,
    );
    onChange(nextItems);
  };

  const updateFeature = (packageIndex, featureIndex, nextValue) => {
    const nextItems = items.map((item, itemIndex) => {
      if (itemIndex !== packageIndex) return item;
      const features = [...(item.features || [])];
      features[featureIndex] = nextValue;
      return { ...item, features };
    });
    onChange(nextItems);
  };

  const addFeature = (packageIndex) => {
    const nextItems = items.map((item, itemIndex) =>
      itemIndex === packageIndex
        ? { ...item, features: [...(item.features || []), ''] }
        : item,
    );
    onChange(nextItems);
  };

  const removeFeature = (packageIndex, featureIndex) => {
    const nextItems = items.map((item, itemIndex) => {
      if (itemIndex !== packageIndex) return item;
      const features = (item.features || []).filter((_, idx) => idx !== featureIndex);
      return { ...item, features: features.length > 0 ? features : [''] };
    });
    onChange(nextItems);
  };

  const addItem = () => onChange([...items, emptyPackage()]);

  const removeItem = (index) => {
    const nextItems = items.filter((_, itemIndex) => itemIndex !== index);
    onChange(nextItems.length > 0 ? nextItems : [emptyPackage()]);
  };

  return (
    <div className="space-y-3">
      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-gray-400">
        Packages
      </label>
      {items.map((item, index) => (
        <div
          key={index}
          className="space-y-2 rounded-lg border border-slate-200 p-3 dark:border-white/10"
        >
          <input
            type="text"
            value={item.name || ''}
            onChange={(e) => updateItem(index, 'name', e.target.value)}
            placeholder="Package name"
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white"
          />
          <input
            type="text"
            value={item.price || ''}
            onChange={(e) => updateItem(index, 'price', e.target.value)}
            placeholder="Price (e.g. $500)"
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white"
          />
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Features</p>
            {(item.features || ['']).map((feature, featureIndex) => (
              <div key={featureIndex} className="flex gap-2">
                <input
                  type="text"
                  value={feature}
                  onChange={(e) => updateFeature(index, featureIndex, e.target.value)}
                  placeholder="Feature"
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white"
                />
                {(item.features || []).length > 1 ? (
                  <button
                    type="button"
                    onClick={() => removeFeature(index, featureIndex)}
                    className="px-2 text-xs font-bold text-red-500"
                  >
                    x
                  </button>
                ) : null}
              </div>
            ))}
            <button
              type="button"
              onClick={() => addFeature(index)}
              className="text-xs font-bold text-primary"
            >
              + Add feature
            </button>
          </div>
          {items.length > 1 ? (
            <button
              type="button"
              onClick={() => removeItem(index)}
              className="text-xs font-bold text-red-500"
            >
              Remove package
            </button>
          ) : null}
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        className="rounded-lg border border-primary/40 px-3 py-1.5 text-xs font-bold text-primary"
      >
        + Add Package
      </button>
    </div>
  );
}
